// Prevent multiple reloads within short timeframe
let isReloading = false;

const reloadWithMessage = (reason) => {
  if (isReloading) return;
  isReloading = true;

  console.warn(`Site updated, reloading... (${reason})`);

  // Use requestIdleCallback for non-blocking reload
  if ("requestIdleCallback" in window) {
    requestIdleCallback(
      () => {
        window.location.reload();
      },
      { timeout: 1000 }
    );
  } else {
    // Small delay to avoid blocking current operations
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }
};

// Optimized error handler
const handleUnhandledRejection = (event) => {
  const reason = event.reason;

  // Quick early returns for common cases
  if (reason === null || reason === undefined) {
    event.preventDefault();
    return;
  }

  // Cache message and stack for performance
  const message = reason?.message || "";
  const stack = reason?.stack || "";

  // Check for errors we want to ignore (not chunk-related)
  const shouldIgnore = [
    // Firebase-related
    () =>
      message.includes("installations/request-failed") ||
      message.includes("PERMISSION_DENIED") ||
      message.includes("analyticsInstance.app") ||
      message.includes("Firebase"),

    // Analytics-related
    () =>
      message.includes("analytics") ||
      stack.includes("analytics") ||
      message.includes("gtag"),

    // Generic load failures (not chunk-specific)
    () => message === "Load failed" && !message.includes("chunk"),

    // TypeErrors (usually app errors)
    () => reason?.name === "TypeError",

    // Network errors
    () =>
      reason?.name === "NetworkError" ||
      reason?.name === "AbortError" ||
      reason?.name === "NotAllowedError" ||
      message.includes("fetch") ||
      message.includes("NetworkError"),
  ];

  // Check if we should ignore this error
  for (const check of shouldIgnore) {
    if (check()) {
      console.warn("Non-chunk error. No reload:", message);
      event.preventDefault();
      return;
    }
  }

  // ONLY reload for SPECIFIC Vite chunk loading failures
  const hasChunkMessage =
    message.includes("Loading chunk") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Loading CSS chunk");

  const hasViteStack =
    stack.includes("__vitePreload") ||
    stack.includes("import()") ||
    stack.includes("vite");

  const hasChunkFilePattern =
    message.includes("/assets/") ||
    (message.includes(".js") && message.includes("chunk"));

  // Only reload if confirmed chunk loading failure
  if (hasChunkMessage && (hasViteStack || hasChunkFilePattern)) {
    console.warn("CONFIRMED chunk loading failure - reloading:", message);
    reloadWithMessage("chunk loading failure");
    return;
  }

  // For everything else, prevent and log
  console.warn("Unhandled rejection (not chunk-related):", message);
  event.preventDefault();
};

// Add event listener for unhandled rejections
window.addEventListener("unhandledrejection", handleUnhandledRejection);

// Official Vite preload error handler
window.addEventListener("vite:preloadError", (event) => {
  console.warn("Vite preload error detected:", event.payload);
  event.preventDefault();
  reloadWithMessage("vite preload error");
});

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";

import "./index.css";
import App from "./App.jsx";

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);
